import { useCallback, useState } from "react";
import {
  apiDeleteRequest,
  apiGetRequest,
  apiPostRequest,
  apiPutRequest,
} from "helpers/Requests";
import {
  URL_SP_SERVICE_AGREEMENT_TEMPLATE,
  URL_SP_SERVICE_AGREEMENT_TEMPLATE_AVAILABLE_FIELDS,
  URL_SP_SERVICE_AGREEMENT_TEMPLATE_PREVIEW,
} from "store/services/URL";
import { toast } from "react-toastify";

const unwrapData = (response) => {
  const body = response?.data ?? response ?? {};
  if (body?.data !== undefined) {
    return body.data;
  }
  return body;
};

const useServiceAgreementTemplate = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [isConfigured, setIsConfigured] = useState(false);
  const [template, setTemplate] = useState(null);
  const [starter, setStarter] = useState(null);
  const [forms, setForms] = useState([]);
  const [selectedFormTemplateId, setSelectedFormTemplateId] = useState(null);
  const [availableFields, setAvailableFields] = useState({
    form_title: null,
    form_fields: [],
    system_fields: [],
    forms: [],
    sp_form_template_id: null,
  });

  const loadTemplate = useCallback(async (spFormTemplateId = null, options = {}) => {
    const silent = Boolean(options?.silent);
    if (!silent) {
      setLoading(true);
    }
    try {
      const query =
        spFormTemplateId != null
          ? `?sp_form_template_id=${encodeURIComponent(spFormTemplateId)}`
          : "";
      const response = await apiGetRequest(
        `${URL_SP_SERVICE_AGREEMENT_TEMPLATE}${query}`
      );
      const payload = unwrapData(response) || {};
      const nextTemplate = payload?.template || null;
      const nextStarter = payload?.starter || null;
      const nextForms = Array.isArray(payload?.forms) ? payload.forms : [];
      setIsConfigured(Boolean(payload?.is_configured));
      setTemplate(nextTemplate);
      setStarter(nextStarter);
      setForms(nextForms);
      setSelectedFormTemplateId(
        payload?.selected_form_template_id ?? nextForms[0]?.id ?? null
      );
      return payload;
    } catch (error) {
      toast.error("Failed to load service agreement template");
      return null;
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, []);

  const loadAvailableFields = useCallback(async (spFormTemplateId = null) => {
    try {
      const query =
        spFormTemplateId != null
          ? `?sp_form_template_id=${encodeURIComponent(spFormTemplateId)}`
          : "";
      const response = await apiGetRequest(
        `${URL_SP_SERVICE_AGREEMENT_TEMPLATE_AVAILABLE_FIELDS}${query}`
      );
      const payload = unwrapData(response) || {};
      setAvailableFields({
        form_title: payload?.form_title || null,
        form_fields: Array.isArray(payload?.form_fields)
          ? payload.form_fields
          : [],
        system_fields: Array.isArray(payload?.system_fields)
          ? payload.system_fields
          : [],
        forms: Array.isArray(payload?.forms) ? payload.forms : [],
        sp_form_template_id: payload?.sp_form_template_id ?? null,
      });
      return payload;
    } catch (error) {
      toast.error("Failed to load available fields");
      return null;
    }
  }, []);

  const saveTemplate = useCallback(async (body, isUpdate = false) => {
    setSaving(true);
    try {
      const request = isUpdate ? apiPutRequest : apiPostRequest;
      const response = await request(URL_SP_SERVICE_AGREEMENT_TEMPLATE, body);
      const saved = unwrapData(response) || null;
      setTemplate(saved);
      setIsConfigured(true);
      const isModeSwitchOnly =
        body?.template_mode &&
        !body?.html_content &&
        !body?.custom_html_content;
      if (isModeSwitchOnly) {
        toast.success(
          body.template_mode === "custom"
            ? "Switched to custom PDF"
            : "Switched to default PDF"
        );
      } else {
        toast.success("Agreement settings saved for this form");
      }
      return saved;
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          "Failed to save service agreement template"
      );
      return null;
    } finally {
      setSaving(false);
    }
  }, []);

  const switchTemplateMode = useCallback(
    async (spFormTemplateId, templateMode) => {
      if (spFormTemplateId == null) {
        toast.error("Please select a form first");
        return null;
      }
      setSaving(true);
      try {
        const response = await apiPutRequest(URL_SP_SERVICE_AGREEMENT_TEMPLATE, {
          sp_form_template_id: Number(spFormTemplateId),
          template_mode: templateMode,
        });
        const saved = unwrapData(response) || null;
        setTemplate(saved);
        setIsConfigured(true);
        toast.success(
          templateMode === "custom"
            ? "Switched to custom PDF"
            : "Switched to default PDF"
        );
        return saved;
      } catch (error) {
        toast.error(
          error?.response?.data?.message || "Failed to switch PDF mode"
        );
        return null;
      } finally {
        setSaving(false);
      }
    },
    []
  );

  const clearTemplate = useCallback(async (spFormTemplateId = null) => {
    if (spFormTemplateId == null) {
      toast.error("Please select a form first");
      return false;
    }
    setClearing(true);
    try {
      const query = `?sp_form_template_id=${encodeURIComponent(
        spFormTemplateId
      )}`;
      const response = await apiDeleteRequest(
        `${URL_SP_SERVICE_AGREEMENT_TEMPLATE}${query}`
      );
      const saved = unwrapData(response) || null;
      if (saved) {
        setTemplate(saved);
      }
      toast.success(
        "Switched to default PDF (your custom draft is kept)"
      );
      return true;
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Failed to clear custom template"
      );
      return false;
    } finally {
      setClearing(false);
    }
  }, []);

  const previewTemplate = useCallback(async (body) => {
    setPreviewing(true);
    try {
      const response = await apiPostRequest(
        URL_SP_SERVICE_AGREEMENT_TEMPLATE_PREVIEW,
        body
      );
      return unwrapData(response) || null;
    } catch (error) {
      toast.error("Failed to preview template");
      return null;
    } finally {
      setPreviewing(false);
    }
  }, []);

  return {
    loading,
    saving,
    clearing,
    previewing,
    isConfigured,
    template,
    starter,
    forms,
    selectedFormTemplateId,
    setSelectedFormTemplateId,
    availableFields,
    loadTemplate,
    loadAvailableFields,
    saveTemplate,
    switchTemplateMode,
    clearTemplate,
    previewTemplate,
  };
};

export default useServiceAgreementTemplate;
